<script lang="ts">
  import { page } from '$app/state';
  import { afterNavigate } from '$app/navigation';

  import { debounce } from '$shared/helpers/utils';
  import GridView from '$shared/organisms/GridView/GridView.svelte';
  import { sentry } from '$shared/helpers/logger/sentry';

  const MaxPollingLimit = 5;

  interface Props {
    invert?: boolean;
    children?: import('svelte').Snippet;
    onpagescroll?: (payload: { id: string }) => void;
  }

  let { invert = false, children, onpagescroll }: Props = $props();

  afterNavigate(() => {
    onRouteChange();
  });

  const dispatchScrollEvent = (e: any): void => {
    dispatchEvent(new CustomEvent('svelte-scroll', { detail: e }));
  };

  const handleScroll = (e: any): void => {
    try {
      const containers = e.target.querySelectorAll('.scroll-list');
      if (!containers && containers.length === 0) {
        return;
      }

      let currentItem;
      for (let i = 0; i < containers.length; i++) {
        const item = containers[i];
        const dim = item?.getBoundingClientRect();

        if (dim.top > 0) {
          currentItem = item;
          break;
        }
      }

      const curId: string = currentItem?.getAttribute('id');
      if (curId) {
        onpagescroll?.({ id: curId });
      }
    } catch (err) {
      sentry.warn('Scroll container :: error on scroll', { error: err });
    }
  };

  const onPageScroll = (e): void => {
    dispatchScrollEvent(e);
    const debounceFunc = debounce(() => handleScroll(e), 1000);
    debounceFunc();
  };

  const onRouteChange = () => {
    const targetId = page.url.searchParams.get('t');

    if (!targetId) {
      contentScrollToTop();
      return;
    }

    contentScrollIntoView(targetId);
  };

  const contentScrollIntoView = (targetId: string, pollCount = 0): void => {
    if (pollCount >= MaxPollingLimit) return;

    // Need to wait for 500 millisec for element to load
    setTimeout(() => {
      const ele = document.getElementById(targetId);
      if (!ele) {
        contentScrollIntoView(targetId, pollCount + 1);
        return;
      }

      ele.scrollIntoView({ behavior: 'smooth' });
    }, 500);
  };

  const contentScrollToTop = () => {
    const element = document.getElementById('scroll-content');
    if (element) {
      element.scrollTop = 0;
    }
  };
</script>

<div class="comp-scroll-container" class:invert>
  <div id="scroll-content" class="content" onscroll={onPageScroll}>
    <GridView>
      {@render children?.()}
    </GridView>
  </div>
</div>

<style>
  .comp-scroll-container {
    width: 100%;
  }
  .content {
    width: 100%;
    height: 100%;
    flex-grow: 1;
    padding: 40px 32px 0;
    overflow: auto;
    background-color: var(--grey-bg-light);
  }
  .comp-scroll-container.invert .content {
    background-color: #fff;
  }
</style>
